import { z } from 'zod';
import { IdSchema, MediaTypeSchema, paginated } from './common.schema';

// Backend entity: Media. Read tolerante a campos faltantes.

// Cuando la request al detalle incluye `area=<slug>`, el back adjunta un
// mini-veredicto a cada item: qué aspectos del control están habilitados
// para editar en esa área. Sin área, no viene.
//
// Aspectos = 'url' (reemplazar archivo), 'metadata' (alt/title), 'placement'
// (isPrimary/sortOrder). Ausencia de `placement` = apagar isPrimary+sortOrder:
// en modo unión, un override de master siempre pisa esos dos campos.
export const MediaItemEditabilitySchema = z.object({
  visibleAspects: z.array(z.enum(['url', 'metadata', 'placement'])).default([]),
  reason: z.string().optional(),
});
export type MediaItemEditability = z.infer<typeof MediaItemEditabilitySchema>;

// `kind` decide el verbo de la fila (release hub-service-00157-vmp):
// - 'source': fila del ingest de una fuente. Verbo = Ocultar/Mostrar.
// - 'masterForSource': imagen master apuntada a un source (override tipo A/B).
//   Verbo = Eliminar (borra fila + link + GCS).
// - 'masterOwn': imagen master pura tipo C. Verbo = Eliminar.
// Peer explícito: NO deducir el kind del string `source` — `master-vtex`
// cubre dos casos distintos y decidir por string falla el 50% de las veces.
// Ausencia del campo = presume 'source' (compat con backends viejos hasta
// que el deploy del contrato se cierre).
export const MediaKindSchema = z.enum(['source', 'masterForSource', 'masterOwn']);
export type MediaKind = z.infer<typeof MediaKindSchema>;

export const MediaSchema = z.object({
  // Backend a veces omite id en respuestas embebidas (ej: producto.media[]).
  // Lo dejamos optional para no bloquear el render del listado; los
  // componentes que necesitan operar (edit/delete) deben chequear presencia.
  id: IdSchema.optional(),
  productId: IdSchema.nullable().optional(),
  type: MediaTypeSchema.optional().default('image'),
  url: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? ''),
  altText: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  sortOrder: z
    .number()
    .int()
    .nullable()
    .optional()
    .transform((v) => v ?? 0),
  isPrimary: z
    .boolean()
    .nullable()
    .optional()
    .transform((v) => v ?? false),
  metadata: z.unknown().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  // Solo viene cuando la request incluyó `area=`. Ausencia = sin gating.
  editability: MediaItemEditabilitySchema.nullable().optional(),
  // Presente solo cuando un master override esta escondiendo una URL
  // distinta que el source sigue publicando. Copy: "Este item tiene un
  // override; source publico ${sourceUrl} despues del override".
  // Revertir con `DELETE /admin/products/:sku/media/:sourceMediaId/master-override`
  // (release b7054cb 2026-08-28). Ausencia = no hay override activo o
  // source no publica algo distinto.
  sourceUrl: z.string().nullable().optional(),
  // Slug del source real, o `master-<slug>` cuando la fila es un override /
  // lineage tipo B. Usado por el UI del override para nombrar la fuente
  // ("axioma publica…").
  source: z.string().nullable().optional(),
  // Presente SOLO cuando la fila fue ocultada por admin para el área activa
  // (release hub-service-00157-vmp / client-gateway-00152-87b). Ausencia =
  // fila visible. Detail response (`POST /products/:sku/query`) marca las
  // ocultas; list response (`POST /products/query`) las filtra fuera. Toggle
  // via `POST/DELETE /products/:sku/media/:sourceMediaId/hide?area=<slug>`.
  hiddenByAdmin: z.boolean().optional(),
  // Discriminador del tipo de fila. Ver `MediaKindSchema` para semántica.
  // Optional: mientras el deploy del back no cierre, ausencia = 'source'.
  kind: MediaKindSchema.optional(),
});
export type Media = z.infer<typeof MediaSchema>;

export const MediaListSchema = paginated(MediaSchema);
export type MediaList = z.infer<typeof MediaListSchema>;

// PATCH /products/:sku/media/:id (sólo metadata, sin re-subir) ------------

export const MediaUpdateSchema = z.object({
  altText: z.string().max(240).optional(),
  title: z.string().max(240).optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  isPrimary: z.boolean().optional(),
  url: z.string().url().optional(),
});
export type MediaUpdate = z.infer<typeof MediaUpdateSchema>;
